import { classnames } from '@bem-react/classnames'
import { PortalWithTheme } from '@consta/uikit/PortalWithTheme'
import { useTheme } from '@consta/uikit/Theme'
import { useClickOutside } from '@consta/uikit/useClickOutside'
import { useForkRef } from '@consta/uikit/useForkRef'
import { useBooleanFlag } from '@library/hooks'
import { useModalManager } from '@library/model'
import type { ElementRef, PropsWithChildren } from 'react'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'

import css from './MovingModal.css'
import type { ModalInterface } from './types'
import { useMove } from './useMove'

export type MovingModalProps = {
  className?: string
  isOpen?: boolean
  anchor?: HTMLElement
}

const FOCUS_Z_INDEX = 1

export const MovingModal = forwardRef<ModalInterface, PropsWithChildren<MovingModalProps>>(
  function MovingModal({ className = '', isOpen: isOpenInitial = false, children, anchor }, ref) {
    const [isOpen, { setFalse: closeModal, setTrue: openModal, toggle: toggleModal }] =
      useBooleanFlag(isOpenInitial)
    const [focus, focusApi] = useBooleanFlag(true)
    const { movingModals } = useModalManager()

    useImperativeHandle(ref, () => ({ openModal, closeModal, toggleModal }), [
      closeModal,
      openModal,
      toggleModal,
    ])

    const [modal, setModal] = useState<HTMLDivElement>()
    const modalRef = useRef<ElementRef<'div'>>(null)

    const forkedRef = useForkRef([setModal, modalRef])

    const { theme } = useTheme()
    useMove(modal, anchor, window.document.body)

    useClickOutside({
      isActive: isOpen,
      ignoreClicksInsideRefs: [modalRef],
      handler: focusApi.setFalse,
    })

    if (!isOpen) {
      return null
    }

    return (
      <PortalWithTheme
        preset={theme}
        container={movingModals.current ?? undefined}
        style={{ zIndex: focus ? FOCUS_Z_INDEX : 'initial', '--focus-z-index': FOCUS_Z_INDEX }}>
        <div
          ref={forkedRef}
          className={classnames(css.container, className)}
          data-focused={focus}
          onClick={focusApi.setTrue}>
          {children}
        </div>
      </PortalWithTheme>
    )
  },
)
